import React, { useState } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Select,
  TimePicker,
  Switch,
  Button,
} from '@crisellevaldez/speculo-ui';

interface AdvancedSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
  // Schedule settings
  schedulingValue: string;
  schedulingUnit: string;
  allowedDays: string[];
  allowedStartTime: string | null;
  allowedEndTime: string | null;
  excludeHolidays: boolean;
  country?: string;
  // Schedule handlers
  onSchedulingValueChange: (value: string) => void;
  onSchedulingUnitChange: (value: string) => void;
  onAllowedDaysChange: (value: string[]) => void;
  onAllowedStartTimeChange: (value: string | null) => void;
  onAllowedEndTimeChange: (value: string | null) => void;
  onExcludeHolidaysChange: (value: boolean) => void;
  onCountryChange?: (value: string) => void;
}

interface ValidationErrors {
  schedulingWindow?: string;
  allowedDays?: string;
  timeRange?: string;
}

export const AdvancedSettingsModal: React.FC<AdvancedSettingsModalProps> = ({
  isOpen,
  onClose,
  readOnly = false,
  // Schedule settings
  schedulingValue,
  schedulingUnit,
  allowedDays,
  allowedStartTime,
  allowedEndTime,
  excludeHolidays,
  country = 'US',
  // Schedule handlers
  onSchedulingValueChange,
  onSchedulingUnitChange,
  onAllowedDaysChange,
  onAllowedStartTimeChange,
  onAllowedEndTimeChange,
  onExcludeHolidaysChange,
  onCountryChange,
}) => {
  const [errors, setErrors] = useState<ValidationErrors>(
    readOnly
      ? {}
      : {
          schedulingWindow: 'Scheduling window is required',
          allowedDays: 'At least one day must be selected',
          timeRange: 'Both start and end time are required',
        }
  );

  const validate = React.useCallback((): boolean => {
    if (readOnly) {
      setErrors({});
      return true;
    }

    const newErrors: ValidationErrors = {};

    // Validate scheduling window
    const schedValue = parseInt(schedulingValue);
    if (!schedValue) {
      newErrors.schedulingWindow = 'Scheduling window is required';
    } else if (schedValue <= 0) {
      newErrors.schedulingWindow = 'Must be a positive number';
    }

    // Validate allowed days
    if (!allowedDays.length) {
      newErrors.allowedDays = 'At least one day must be selected';
    }

    // Validate time range
    if (!allowedStartTime || !allowedEndTime) {
      newErrors.timeRange = 'Both start and end time are required';
    } else {
      const startHour = parseInt(allowedStartTime.split(':')[0]);
      const endHour = parseInt(allowedEndTime.split(':')[0]);
      if (startHour >= endHour) {
        newErrors.timeRange = 'Start time must be before end time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [schedulingValue, allowedDays, allowedStartTime, allowedEndTime, setErrors, readOnly]);

  // Run validation whenever fields change
  React.useEffect(() => {
    validate();
  }, [schedulingValue, allowedDays, allowedStartTime, allowedEndTime, validate]);

  const handleContinue = () => {
    if (validate()) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      onClose={() => {
        if (Object.keys(errors).length === 0) {
          onClose();
        }
      }}
    >
      <Modal.Header
        onClose={() => {
          if (Object.keys(errors).length === 0) {
            onClose();
          }
        }}
      >
        <Typography variant="h3" className="font-bold">
          Advanced Settings
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          {/* Scheduling Window */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Control>
              <Form.Label>Scheduling Window</Form.Label>
              <Input
                className="min-h-[40px]"
                placeholder="Enter value"
                type="number"
                value={schedulingValue}
                onChange={(e) => {
                  if (!readOnly) {
                    const value = e.target.value;
                    // Only allow positive numbers
                    if (!value || parseInt(value) > 0) {
                      onSchedulingValueChange(value);
                    }
                  }
                }}
                helperText="Set the range for when the AI can schedule calls."
                error={errors.schedulingWindow}
                disabled={readOnly}
              />
            </Form.Control>
            <Form.Control>
              <Form.Label>&nbsp;</Form.Label>
              <Select
                placeholder="Select unit"
                value={schedulingUnit}
                onChange={(value) => !readOnly && onSchedulingUnitChange(value as string)}
                options={[
                  { value: 'days', label: 'Days' },
                  { value: 'hours', label: 'Hours' },
                ]}
                disabled={readOnly}
              />
            </Form.Control>
          </div>

          {/* Allowed Days */}
          <Form.Control>
            <Form.Label>Allowed Days</Form.Label>
            <Select
              placeholder="Select days"
              multiple={true}
              value={allowedDays}
              onChange={(value) => {
                if (!readOnly && Array.isArray(value)) {
                  onAllowedDaysChange(value);
                }
              }}
              disabled={readOnly}
              options={[
                { value: 'Sunday', label: 'Sunday' },
                { value: 'Monday', label: 'Monday' },
                { value: 'Tuesday', label: 'Tuesday' },
                { value: 'Wednesday', label: 'Wednesday' },
                { value: 'Thursday', label: 'Thursday' },
                { value: 'Friday', label: 'Friday' },
                { value: 'Saturday', label: 'Saturday' },
              ]}
              helperText="Choose the days of the week when calls can be scheduled."
              error={errors.allowedDays}
            />
          </Form.Control>

          {/* Allowed Hours */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Control>
              <Form.Label>Start Time</Form.Label>
              <TimePicker
                placeholder="Select start time"
                value={allowedStartTime}
                onChange={(time) => !readOnly && onAllowedStartTimeChange(time)}
                minTime="08:00"
                maxTime="20:00"
                step={60}
                helperText="Set time range for when calls can occur."
                error={errors.timeRange}
                disabled={readOnly}
              />
            </Form.Control>
            <Form.Control>
              <Form.Label>End Time</Form.Label>
              <TimePicker
                placeholder="Select end time"
                value={allowedEndTime}
                onChange={(time) => !readOnly && onAllowedEndTimeChange(time)}
                minTime="08:00"
                maxTime="20:00"
                step={60}
                disabled={readOnly}
              />
            </Form.Control>
          </div>

          {/* Country and Exclude Holidays */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Control>
              <Form.Label>Country</Form.Label>
              <Select
                placeholder="Select country"
                value={country}
                onChange={(value) =>
                  !readOnly && onCountryChange && onCountryChange(value as string)
                }
                options={[
                  { value: 'US', label: 'United States' },
                  { value: 'CA', label: 'Canada' },
                ]}
                disabled={readOnly}
                helperText="Select the country for holiday exclusions."
              />
            </Form.Control>
            <Form.Control className="flex items-center">
              <div className="flex mt-5 gap-2">
                <Form.Label className="mb-0">Exclude Holidays</Form.Label>
                <Switch
                  size="sm"
                  checked={excludeHolidays}
                  onChange={(e) => !readOnly && onExcludeHolidaysChange(e.target.checked)}
                  disabled={readOnly}
                />
              </div>
            </Form.Control>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {!readOnly && (
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={handleContinue}
              disabled={Object.keys(errors).length > 0}
            >
              Continue
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};
